// user-data-service.js
// Data access layer for users with offline/online abstraction

import axios from "axios";

class UserDataService {
  constructor() {
    this.isElectron = window && window.electron;
    this.isOffline = this.isElectron;
  }

  // Check if current user is MASTER in offline mode
  isMasterUserOffline() {
    // In offline mode, we need to get user data from local storage or database
    // For now, we'll check localStorage where user data might be stored
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user && user.rank === 'MASTER' && user.vessel_id;
      }
    } catch (e) {
      console.warn('Failed to parse user data from localStorage:', e);
    }
    return false;
  }

  // Get MASTER user's vessel ID in offline mode
  getMasterVesselIdOffline() {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        return user && user.vessel_id ? user.vessel_id : null;
      }
    } catch (e) {
      console.warn('Failed to parse user data from localStorage:', e);
    }
    return null;
  }

  /**
   * Get all users with optional role filtering
   * @param {Object} filters - Filter options
   * @param {number} filters.roleId - Role ID to filter by (3=TEACHER, 4=CANDIDATE)
   * @param {string} filters.search - Search term for name/email
   * @param {boolean} filters.active - Filter by active status
   * @param {number} filters.page - Page number for pagination
   * @param {number} filters.limit - Items per page
   * @param {string} filters.rank - Filter by rank
   * @param {number} filters.vessel_id - Filter by vessel ID
   */
  async getUsers(filters = {}) {
    if (this.isOffline) {
      return this.getUsersOffline(filters);
    } else {
      return this.getUsersOnline(filters);
    }
  }

  /**
   * Get candidates (role_id = 4)
   */
  async getCandidates(filters = {}) {
    return this.getUsers({ ...filters, roleId: 4 });
  }

  /**
   * Get teachers (role_id = 3)
   */
  async getTeachers(filters = {}) {
    return this.getUsers({ ...filters, roleId: 3 });
  }

  /**
   * Get user by ID
   */
  async getUser(id) {
    if (this.isOffline) {
      return this.getUserOffline(id);
    } else {
      return this.getUserOnline(id);
    }
  }

  /**
   * Get user statistics
   */
  async getUserStats() {
    if (this.isOffline) {
      return this.getUserStatsOffline();
    } else {
      return this.getUserStatsOnline();
    }
  }

  // OFFLINE METHODS
  async getUsersOffline(filters = {}) {
    // Check if user is MASTER and has vessel_id for offline mode
    const isMaster = this.isMasterUserOffline();
    console.log('[UserDataService] getUsersOffline - isMasterUser:', isMaster, 'roleId filter:', filters.roleId);

    if (isMaster && filters.roleId == 4) {
      const vesselId = this.getMasterVesselIdOffline();
      console.log('[UserDataService] Master user vessel_id:', vesselId);
      if (vesselId) {
        filters.master_vessel_id = vesselId;
      } else {
        console.log('[UserDataService] Master user has no vessel_id - skipping vessel filter');
      }
    }
    return new Promise((resolve, reject) => {
      if (!window.electron || !window.electron.query) {
        reject(new Error("Electron query interface not available"));
        return;
      }

      // Build WHERE clause
      let whereClause = `WHERE (is_deleted = 0 OR is_deleted IS NULL)`;
      const params = [];

      // Filter by role ID - check both column and JSON data field
      if (filters.roleId) {
        whereClause += ` AND (role_id = ? OR json_extract(data, '$.role_id') = ?)`;
        params.push(filters.roleId, filters.roleId);
      }

      // Filter by multiple role IDs (e.g. [2, 6] for admins + managers)
      if (filters.roleIds) {
        const ids = (Array.isArray(filters.roleIds)
          ? filters.roleIds
          : String(filters.roleIds).split(","))
          .map((x) => parseInt(x))
          .filter(Boolean);
        if (ids.length) {
          const clause = ids
            .map(() => `(role_id = ? OR json_extract(data, '$.role_id') = ?)`)
            .join(" OR ");
          whereClause += ` AND (${clause})`;
          ids.forEach((id) => params.push(id, id));
        }
      }

      // Filter by active status - treat NULL as active (default)
      if (typeof filters.active === "boolean") {
        if (filters.active) {
          // Active users: include both active=1 and NULL (default to active)
          whereClause += ` AND (active = 1 OR active IS NULL)`;
        } else {
          // Inactive users: only active=0
          whereClause += ` AND active = 0`;
        }
      }

      // Filter by rank
      if (filters.rank) {
        whereClause += ` AND rank = ?`;
        params.push(filters.rank);
      }

      // Filter by vessel_id
      // Special filter for MASTER users - only show candidates from their vessel
      if (filters.master_vessel_id) {
        // Only apply this filter for candidates (role ID 4)
        if (filters.roleId == 4 || filters.roleId == null) {
          whereClause += ` AND vessel_id = ?`;
          params.push(filters.master_vessel_id);
        }
      } else if (filters.vessel_id) {
        whereClause += ` AND vessel_id = ?`;
        params.push(filters.vessel_id);
      }

      // Search filter
      if (filters.search && filters.search.trim()) {
        whereClause += ` AND (name LIKE ? OR email LIKE ? OR phone LIKE ? OR rank LIKE ?)`;
        const searchTerm = `%${filters.search.trim()}%`;
        params.push(searchTerm, searchTerm, searchTerm, searchTerm);
      }

      // First get total count for pagination
      const countQuery = `SELECT COUNT(*) as total FROM users ${whereClause}`;

      console.log('[UserDataService] Query:', countQuery);
      console.log('[UserDataService] Params:', params);

      window.electron
        .query(countQuery, params)
        .then((countResult) => {
          const totalCount = countResult[0] ? countResult[0].total : 0;

          // Now get paginated data
          let dataQuery = `
            SELECT
              id, name, email, phone, rank, age, contract_expiry, on_board_since, vessel_id,
              role_id, role_name, active, is_approved, is_deleted, created_at, updated_at, data
            FROM users
            ${whereClause}
            ORDER BY id DESC
          `;

          const dataParams = [...params];

          // Pagination
          if (filters.limit) {
            const offset = ((filters.page || 1) - 1) * filters.limit;
            dataQuery += ` LIMIT ? OFFSET ?`;
            dataParams.push(filters.limit, offset);
          }

          return window.electron.query(dataQuery, dataParams).then((rows) => ({ rows, totalCount }));
        })
        .then(({ rows, totalCount }) => {
          // Parse JSON data field and merge with row data
          const users = rows.map((row) => {
            try {
              const fullData = JSON.parse(row.data || "{}");
              
              // Use roles from fullData if available, otherwise create from role columns
              let roles = fullData.roles || [];
              if (row.role_id && row.role_name && roles.length === 0) {
                roles = [{
                  id: row.role_id,
                  name: row.role_name,
                  pivot: { user_id: row.id, role_id: row.role_id },
                  permissions: []
                }];
              }
              
              return {
                ...row,
                ...fullData,
                // Override with our structured data (prefer row if available, fallback to fullData)
                id: row.id,
                name: row.name || fullData.name,
                email: row.email || fullData.email,
                phone: row.phone || fullData.phone || '',
                rank: row.rank || fullData.rank || '',
                age: row.age || fullData.age || '',
                contract_expiry: row.contract_expiry || fullData.contract_expiry || '',
                on_board_since: row.on_board_since || fullData.on_board_since || '',
                vessel_id: row.vessel_id || fullData.vessel_id || '',
                roles: roles,
                role_name: row.role_name || (roles[0] ? roles[0].name : ''),
                role_id: row.role_id || (roles[0] ? roles[0].id : null),
                active: Boolean(row.active !== undefined ? row.active : fullData.active),
                is_approved: Boolean(row.is_approved !== undefined ? row.is_approved : fullData.is_approved),
                is_deleted: Boolean(row.is_deleted !== undefined ? row.is_deleted : fullData.is_deleted),
              };
            } catch (error) {
              console.warn("Failed to parse user data JSON:", error);
              // Create roles from database columns even if JSON parsing fails
              const roles = row.role_id && row.role_name ? [{
                id: row.role_id,
                name: row.role_name,
                pivot: { user_id: row.id, role_id: row.role_id },
                permissions: []
              }] : [];
              
              return {
                ...row,
                roles: roles,
                role_name: row.role_name || '',
                role_id: row.role_id || null,
                active: Boolean(row.active),
                is_approved: Boolean(row.is_approved),
                is_deleted: Boolean(row.is_deleted),
              };
            }
          });

          resolve({
            data: users,
            count: totalCount, // Total count for pagination
            page: filters.page || 1,
            limit: filters.limit || users.length,
          });
        })
        .catch(reject);
    });
  }

  async getUserOffline(id) {
    return new Promise((resolve, reject) => {
      if (!window.electron || !window.electron.query) {
        reject(new Error("Electron query interface not available"));
        return;
      }

      const query = `
        SELECT 
          id, name, email, phone, rank, role_id, role_name,
          active, is_approved, is_deleted, created_at, updated_at, data
        FROM users 
        WHERE id = ? AND is_deleted = 0
      `;

      window.electron
        .query(query, [id])
        .then((rows) => {
          if (rows.length === 0) {
            reject(new Error("User not found"));
            return;
          }

          const row = rows[0];
          try {
            const fullData = JSON.parse(row.data || "{}");
            
            // Use roles from fullData if available, otherwise create from role columns
            let roles = fullData.roles || [];
            if (row.role_id && row.role_name && roles.length === 0) {
              roles = [{
                id: row.role_id,
                name: row.role_name,
                pivot: { user_id: row.id, role_id: row.role_id },
                permissions: []
              }];
            }
            
            const user = {
              ...row,
              ...fullData,
              // Override with our structured data
              id: row.id,
              name: row.name,
              email: row.email,
              phone: row.phone,
              rank: row.rank,
              roles: roles,
              role_name: row.role_name || (roles[0] ? roles[0].name : ''),
              role_id: row.role_id || (roles[0] ? roles[0].id : null),
              active: Boolean(row.active),
              is_approved: Boolean(row.is_approved),
              is_deleted: Boolean(row.is_deleted),
            };
            resolve({ data: user });
          } catch (error) {
            console.warn("Failed to parse user data JSON:", error);
            // Create roles from database columns even if JSON parsing fails
            const roles = row.role_id && row.role_name ? [{
              id: row.role_id,
              name: row.role_name,
              pivot: { user_id: row.id, role_id: row.role_id },
              permissions: []
            }] : [];
            
            const user = {
              ...row,
              roles: roles,
              role_name: row.role_name || '',
              role_id: row.role_id || null,
              active: Boolean(row.active),
              is_approved: Boolean(row.is_approved),
              is_deleted: Boolean(row.is_deleted),
            };
            resolve({ data: user });
          }
        })
        .catch(reject);
    });
  }

  async getUserStatsOffline() {
    return new Promise((resolve, reject) => {
      if (!window.electron || !window.electron.query) {
        reject(new Error("Electron query interface not available"));
        return;
      }

      const query = `
        SELECT 
          COUNT(*) as total_users,
          SUM(CASE WHEN role_id = 4 THEN 1 ELSE 0 END) as total_candidates,
          SUM(CASE WHEN role_id = 3 THEN 1 ELSE 0 END) as total_teachers,
          SUM(CASE WHEN active = 1 THEN 1 ELSE 0 END) as active_users,
          SUM(CASE WHEN is_approved = 1 THEN 1 ELSE 0 END) as approved_users
        FROM users 
        WHERE is_deleted = 0
      `;

      window.electron
        .query(query, [])
        .then((rows) => {
          resolve({
            data: rows[0] || {
              total_users: 0,
              total_candidates: 0,
              total_teachers: 0,
              active_users: 0,
              approved_users: 0,
            },
          });
        })
        .catch(reject);
    });
  }

  // ONLINE METHODS
  async getUsersOnline(filters = {}) {
    const params = {};

    if (filters.roleId) {
      params.roleId = filters.roleId;
    }

    if (filters.roleIds) {
      params.roleIds = Array.isArray(filters.roleIds)
        ? filters.roleIds.join(",")
        : filters.roleIds;
    }

    if (filters.search) {
      params.search_keyword = filters.search;
    }

    if (typeof filters.active === "boolean") {
      params.is_active = filters.active ? "1" : "0";
    }

    if (filters.page) {
      params.page = filters.page;
    }

    if (filters.limit) {
      params.rowsPerPage = filters.limit;
    }

    if (filters.rank) {
      params.rank = filters.rank;
    }

    if (filters.vessel_id) {
      params.vessel_id = filters.vessel_id;
    }

    const response = await axios.get("/users", { params });
    return response.data;
  }

  async getUserOnline(id) {
    const response = await axios.get(`/users/${id}`);
    return response.data;
  }

  async getUserStatsOnline() {
    const response = await axios.get("/users/stats");
    return response.data;
  }

  // UTILITY METHODS
  getRoleName(roleId) {
    const roleMap = {
      1: "SUPER ADMIN",
      2: "ADMIN",
      3: "TEACHER",
      4: "CANDIDATE",
      5: "COMPANY",
      6: "MANAGER",
    };
    return roleMap[roleId] || "UNKNOWN";
  }

  isCandidate(user) {
    return user.roles[0].id === 4 || user.roles[0].name === "CANDIDATE";
  }

  isTeacher(user) {
    return user.roles[0].id === 3 || user.roles[0].name === "TEACHER";
  }

  isAdmin(user) {
    return (
      user.roles[0].id === 2 ||
      user.roles[0].name === "ADMIN" ||
      user.roles[0].id === 6 ||
      user.roles[0].name === "MANAGER"
    );
  }
}

export default new UserDataService();
